import { useMemo } from 'react';

import type { PermissionName } from '../../constants/permissions';

import { useGlobalState } from '../../../../global_state/src';
import { IS_PROD } from '../../utils/env-util';

/**
 * All permission names currently granted to the user, after applying any
 * devbar-only frontend simulation. Overrides are ignored in production so
 * they can never mask a real permission check for real users.
 */
const useEffectivePermissionNames = (): ReadonlySet<string> => {
    const [buyingProfile] = useGlobalState('buyingProfile');
    const [permissionOverrides] = useGlobalState('permissionOverrides');

    return useMemo(() => {
        const backendPermissionNames = new Set(
            buyingProfile?.permissions.map((p) => p.permissionName) ?? []
        );

        if (IS_PROD) return backendPermissionNames;

        for (const [permissionName, isGranted] of Object.entries(
            permissionOverrides ?? {}
        )) {
            if (isGranted) {
                backendPermissionNames.add(permissionName);
            } else {
                backendPermissionNames.delete(permissionName);
            }
        }

        return backendPermissionNames;
    }, [buyingProfile?.permissions, permissionOverrides]);
};

const useHasPermission = (permissionName: PermissionName): boolean => {
    const effectivePermissionNames = useEffectivePermissionNames();
    return effectivePermissionNames.has(permissionName);
};

const useHasAnyPermission = (
    permissionNames: ReadonlyArray<PermissionName>
): boolean => {
    const effectivePermissionNames = useEffectivePermissionNames();
    return permissionNames.some((permissionName) =>
        effectivePermissionNames.has(permissionName)
    );
};

export { useEffectivePermissionNames, useHasAnyPermission, useHasPermission };
